import { VIZ_CATEGORICAL } from '@splunk/visualization-color-palettes';
import {
    COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
    enhanceOptionsSchemaWithDynamicOptions,
    getPattern,
} from '../utils';

export const timelineOptionsSchema = enhanceOptionsSchemaWithDynamicOptions({
    x: {
        default: '> primary | seriesByType("time")',
        description: 'Specify the data source applied to the x-axis.',
        type: 'array',
        items: { type: ['string', 'number'] },
    },
    y: {
        default: '> primary | seriesByPrioritizedTypes("string", "number")',
        description: 'Specify the data source applied to the y-axis.',
        type: 'array',
        items: { type: ['string', 'number'] },
    },
    duration: {
        default: '> primary | seriesByPrioritizedTypes("number", "time")',
        description:
            'Specify the data source to apply duration to events. This will display events over a length of time as a bar rather than a circle.',
        type: 'array',
        items: { type: ['string', 'number'] },
    },
    category: {
        default: '> primary| seriesByName("category")',
        description:
            'Specify the data source to apply series categories. This will color events in distinct categories.',
        type: 'array',
        items: { type: ['string', 'number'] },
    },
    xField: {
        default: '> x | getField()',
        description: 'Specify the field that should be mapped to the x-axis.',
        type: 'string',
    },
    yField: {
        default: '> y | getField()',
        description: 'Specify the field that should be mapped to the y-axis.',
        type: 'string',
    },
    durationField: {
        default: '> duration | getField()',
        description: 'Specify the field that should be mapped to the x-axis.',
        type: 'string',
    },
    durationUnit: {
        default: 'milliseconds',
        description:
            'Specify the unit of the values in the duration data source used to apply duration to events.',
        pattern: getPattern(['milliseconds', 'seconds', 'minutes', 'hours']),
        type: 'string',
    },
    categoryField: {
        default: '> category | getField()',
        description: 'Specify the field that should be mapped to the y-axis.',
        type: 'string',
    },
    additionalTooltipFields: {
        default: [],
        description:
            'Specify the fields to add to the default set of tooltips. Tooltips appear when you hover over events. These fields and their corresponding values are shown in addition to the ones displayed by default.',
        type: 'array',
        items: { type: 'string' },
    },
    backgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color for the background. You may use a data source to apply the color. The default for enterprise light is "#ffffff". The default for enterprise dark is "#000000". The default for prisma dark is "#0b0c0e".',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    dataColors: {
        description:
            'Specify the colors to use for events. You can use an array of colors to be picked from, or you can use a data source to apply dynamic coloring. For example, “> primary | seriesByName("count") | rangeValue(lowToHigh)”. By default, the dataColors option is not defined and seriesColors is used for categorical coloring.',
        type: ['string', 'array'],
        items: { type: 'string' },
    },
    legendDisplay: {
        default: 'off',
        description: 'Specify the location of the legend on the panel.',
        pattern: getPattern(['right', 'bottom', 'off']),
        type: 'string',
    },
    legendTruncation: {
        default: 'ellipsisEnd',
        description:
            'Specify how to display legend labels when they overflow the layout boundaries by replacing overflow text with an ellipsis.',
        pattern: getPattern(['ellipsisEnd', 'ellipsisMiddle', 'ellipsisStart', 'ellipsisOff']),
        type: 'string',
    },
    resultLimit: {
        default: 10000,
        description:
            'Specify the maximum number of events to render on the timeline. Adjust the value to improve performance.',
        type: 'number',
    },
    seriesColors: {
        default: VIZ_CATEGORICAL,
        description: 'Specify the colors used for a series. For example, ["#FF0000", "#0000FF", "#008000"].',
        type: 'array',
        items: { type: 'string' },
    },
    yAxisLabelWidth: {
        default: 100,
        description: 'Specify the width, in pixels, for y-axis labels.',
        type: 'number',
    },
});
